const SENTINEL = 'ig.field.type.delete';

/**
 * Convert RTF to HTML for the rich text editor.
 * Handles bold, italic, underline, and paragraph breaks.
 * Non-RTF strings are wrapped in <p> tags as plain text.
 */
export function rtfToHtml(rtf: string): string {
  if (!rtf || rtf === SENTINEL) return '<p></p>';
  const s = rtf.trim();

  if (!s.startsWith('{\\rtf')) {
    // Plain text — preserve paragraph breaks
    const html = s
      .split(/\n\n+/)
      .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
      .join('');
    return html || '<p></p>';
  }

  let t = s;

  // Strip structural groups we don't need
  t = t.replace(/\{\\fonttbl[^{}]*(?:\{[^{}]*\}[^{}]*)*/g, '');
  t = t.replace(/\{\\colortbl[^{}]*\}/g, '');
  t = t.replace(/\{\\stylesheet[^{}]*(?:\{[^{}]*\}[^{}]*)*/g, '');
  t = t.replace(/\{\\info[^{}]*(?:\{[^{}]*\}[^{}]*)*/g, '');
  // Strip outer {\rtf1...<body>} wrapper
  t = t.replace(/^\{\\rtf\d+[^\n]*\n?/, '').replace(/\}$/, '');

  // Map RTF control sequences → HTML
  // Bold
  t = t.replace(/\{\\b\s([\s\S]*?)\}/g, '<strong>$1</strong>');
  t = t.replace(/\\b\s([\s\S]*?)\\b0\s?/g, '<strong>$1</strong>');
  // Italic
  t = t.replace(/\{\\i\s([\s\S]*?)\}/g, '<em>$1</em>');
  t = t.replace(/\\i\s([\s\S]*?)\\i0\s?/g, '<em>$1</em>');
  // Underline
  t = t.replace(/\{\\ul\s([\s\S]*?)\}/g, '<u>$1</u>');
  t = t.replace(/\\ul\s([\s\S]*?)\\ulnone\s?/g, '<u>$1</u>');
  // Paragraph + line breaks
  t = t.replace(/\\pard[^\\\n]*/g, '');
  t = t.replace(/\\par\b\s*/g, '</p><p>');
  t = t.replace(/\\line\b\s*/g, '<br>');
  // Strip remaining control words and symbols
  t = t.replace(/\\[a-z*]+[-]?\d*\s?/gi, '');
  t = t.replace(/[{}]/g, '');
  // Wrap
  t = `<p>${t.trim()}</p>`;
  // Clean up whitespace and empty paras
  t = t.replace(/<p>\s*<\/p>/g, '').trim();

  return t || '<p></p>';
}

/**
 * Convert TipTap HTML back to RTF for storage in Iglu.
 * Returns sentinel if the content is effectively empty.
 */
export function htmlToRtf(html: string): string {
  if (!html) return SENTINEL;
  // Strip whitespace-only / empty editor state
  const stripped = html.replace(/<[^>]+>/g, '').replace(/\s/g, '');
  if (!stripped) return SENTINEL;

  let t = html;

  // Formatting tags → RTF groups
  t = t.replace(/<strong>([\s\S]*?)<\/strong>/gi, '{\\b $1}');
  t = t.replace(/<b>([\s\S]*?)<\/b>/gi, '{\\b $1}');
  t = t.replace(/<em>([\s\S]*?)<\/em>/gi, '{\\i $1}');
  t = t.replace(/<i>([\s\S]*?)<\/i>/gi, '{\\i $1}');
  t = t.replace(/<u>([\s\S]*?)<\/u>/gi, '{\\ul $1}');
  // Paragraph breaks
  t = t.replace(/<\/p>\s*<p>/gi, '\\par\n');
  t = t.replace(/<p>/gi, '').replace(/<\/p>/gi, '\\par\n');
  t = t.replace(/<br\s*\/?>/gi, '\\line\n');
  // Strip remaining tags
  t = t.replace(/<[^>]+>/g, '');
  // HTML entities
  t = t.replace(/&amp;/g, '&')
       .replace(/&lt;/g, '<')
       .replace(/&gt;/g, '>')
       .replace(/&nbsp;/g, ' ')
       .replace(/&#39;/g, "'")
       .replace(/&quot;/g, '"');

  const body = t.trim().replace(/\\par\s*$/, '').trim();

  return `{\\rtf1\\ansi\\deff0\\deflang1033{\\fonttbl{\\f0\\froman\\fcharset0 Times New Roman;}}\\viewkind4\\uc1\\pard\\f0\\fs24 ${body}}`;
}
